import React, { useEffect, useState } from 'react';
import { X, Download, Loader2, FileText } from 'lucide-react';
import { fetchExecAttachmentBlob } from '../../utils/execInboxApi';

interface AttachmentMeta {
  id: string;
  filename: string;
  mimeType: string;
  size: number;
}

interface Props {
  mailboxUserId: string;
  messageId: string;
  attachment: AttachmentMeta;
  onClose: () => void;
  onToast: (msg: string, error?: boolean) => void;
}

export function isPreviewable(mimeType: string): boolean {
  return mimeType.startsWith('image/') || mimeType === 'application/pdf';
}

const ExecAttachmentPreviewModal: React.FC<Props> = ({
  mailboxUserId, messageId, attachment, onClose, onToast,
}) => {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;
    setLoading(true);
    setUrl(null);
    setError(null);
    fetchExecAttachmentBlob(mailboxUserId, messageId, attachment.id, {
      filename: attachment.filename,
      mimeType: attachment.mimeType,
    })
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(new Blob([blob], { type: attachment.mimeType }));
        setUrl(objectUrl);
      })
      .catch((e) => { if (!cancelled) setError((e as Error).message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [mailboxUserId, messageId, attachment.id, attachment.mimeType, attachment.filename]);

  const handleDownload = () => {
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = attachment.filename;
    a.click();
  };

  const isImage = attachment.mimeType.startsWith('image/');
  const isPdf = attachment.mimeType === 'application/pdf';

  return (
    <>
      <div className="exec-preview-overlay" onClick={onClose} />
      <div className="exec-preview-modal" role="dialog" aria-label={`Preview ${attachment.filename}`}>
        <div className="exec-preview-head">
          <span className="exec-preview-title" title={attachment.filename}>{attachment.filename}</span>
          <div className="exec-preview-actions">
            <button type="button" className="exec-ghost-btn" disabled={!url} onClick={handleDownload}>
              <Download size={13} /> Download
            </button>
            <button type="button" className="exec-so-close" onClick={onClose} aria-label="Close"><X size={16} /></button>
          </div>
        </div>
        <div className="exec-preview-body">
          {loading && <div className="exec-empty"><Loader2 size={20} className="spin" /><p>Loading preview…</p></div>}
          {!loading && error && (
            <div className="exec-empty"><p>{error}</p></div>
          )}
          {!loading && !error && url && isImage && (
            <img src={url} alt={attachment.filename} />
          )}
          {!loading && !error && url && isPdf && (
            <iframe src={url} title={attachment.filename} />
          )}
          {!loading && !error && url && !isImage && !isPdf && (
            <div className="exec-empty">
              <FileText size={22} />
              <p>No inline preview for this file type.</p>
              <button type="button" className="exec-cta" onClick={handleDownload}>Download instead</button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ExecAttachmentPreviewModal;
